import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Risk, FundType, Umbrella, PurchasableFund } from 'funds-model';
import { Actions } from './FundList';
import fundListOptions from './fundListOptions';
import { FilterState } from './useFilterState';
import { DropdownOptionModel } from '../form/Dropdown/tools/Dropdown.model';
import { AutocompleteOptionModel } from '../form/Autocomplete/tools/Autocomplete.model';
import { toDropdownTranslateOptions } from '../form/Dropdown/tools/Dropdown.utils';
import { FilterActionPayload } from './tools/FundList.model';

const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export interface FundListFilterConfigMulti {
  clearFilter: () => void;
  filterDelete: (x: string[] | string | undefined) => void;
  onChange: (x: string[]) => void;
  value: string[];
  valueToLabel?: (x: string) => string;
  labelId: string;
  placeholder: string;
  label: string;
  options: string[];
  enableListenerOfExternalValueChange?: boolean;
  name: string;
}

export interface FundListFilterConfigUnitCategory {
  clearFilter: () => void;
  onChange: (event: React.ChangeEvent<{ name?: string; value: unknown; }>) => void
  value: string;
  labelId: string;
  label: string;
  placeholder: string;
  options: DropdownOptionModel[];
  listTitle: string;
  placeholderDisplayType: 'dynamic';
  name: string;
}

export interface FundListFilterCofingSearch {
  onChange: (event: React.ChangeEvent<{ name?: string; value: unknown; }>) => void;
  value: string;
  clearFilter: () => void;
  name: string;
}

export interface FundListFilterCofingAutocomplete {
  onChange: (option: AutocompleteOptionModel) => void;
  value: AutocompleteOptionModel;
  clearFilter: () => void;
  placeholder: string;
  searchPlaceholder: string;
  getOptionLabel: (option: AutocompleteOptionModel) => string;
  options: AutocompleteOptionModel[];
  name: string;
}

export interface FundListFilterCofingCheckbox {
  onChange: (x: boolean) => void;
  value: boolean;
  clearFilter: () => void;
  checked: boolean;
}

export interface FundListFilterConfig {
  risk: FundListFilterConfigMulti;
  fundType: FundListFilterConfigMulti;
  umbrella: FundListFilterConfigMulti;
  horizon: FundListFilterConfigMulti;
  unitCategory: FundListFilterConfigUnitCategory;
  search: FundListFilterCofingSearch;
  autocomplete: FundListFilterCofingAutocomplete;
  adequacyWithTargetMarket: FundListFilterCofingCheckbox;
  wallet: FundListFilterCofingCheckbox;
  esg: FundListFilterCofingCheckbox;
  clearFilters: () => void;
  isAnyFilter: boolean;
}

const useRiskFilterConfig = (actions: Actions, risks: Risk[], filters: FilterState): FundListFilterConfigMulti => {
  const [t] = useTranslation('components');
  const [fundRisk, setFundRisk] = useState<string[]>(filters.risk);
  return {
    onChange: (arrValue: string[]) => {
      setFundRisk(arrValue);
      const payload = arrValue.map((v: string) => v.toLowerCase());
      actions.addRiskFilter(payload as FilterActionPayload);
    },
    clearFilter: () => setFundRisk([]),
    filterDelete: (value?: string | string[]) => setFundRisk(prevState => prevState.filter(fundRiskOption => fundRiskOption !== value)),
    value: fundRisk,
    labelId: 'dropdown-risk',
    placeholder: t('fund-list.choose'),
    label: t('fund-list.risk'),
    options: risks.map(riskOption => riskOption.name),
    valueToLabel: (value: string) => risks.find(risk => risk.name === value)?.filterLabel || value,
    enableListenerOfExternalValueChange: true,
    name: 'funds-risk-filter'
  }
}

const useFundTypeFilterConfig = (actions: Actions, initialFilterFundType: string | undefined, fundTypes: FundType[], filters: FilterState): FundListFilterConfigMulti => {
  const [t] = useTranslation('components');
  const [fundType, setFundType] = useState<string[]>(filters.fundType ? filters.fundType.map(value => capitalizeFirstLetter(
      value)) : []);

  return {
    onChange: (arrValue: string[]) => {
      setFundType(arrValue);
      const payload = arrValue.map((v: string) => v.toLowerCase());
      actions.addFundTypeFilter(payload as FilterActionPayload);
    },
    clearFilter: () => setFundType([]),
    filterDelete: (value?: string | string[]) => setFundType(prevState => prevState.filter(fundTypeOption => fundTypeOption !== value)),
    value: fundType,
    labelId: 'dropdown-fundType',
    placeholder: t('fund-list.choose'),
    label: t('fund-list.fundType'),
    options: fundTypes.map(fundTypeOption => capitalizeFirstLetter(fundTypeOption.name)),
    enableListenerOfExternalValueChange: true,
    name: 'funds-type-filter'
  }
}

const useFundUmbrellaFilterConfig = (actions: Actions, allAvailableFunds: PurchasableFund[] | undefined, umbrellas: Umbrella[], filters: FilterState): FundListFilterConfigMulti => {
  const [t] = useTranslation('components');
  const umbrellasInFunds = allAvailableFunds ? allAvailableFunds.map(fund => fund.umbrella?.name) : [];
  const availableUmbrellas = umbrellas.filter(umbrella => umbrellasInFunds.includes(umbrella.name));
  const [fundUmbrella, setFundUmbrella] = useState<string[]>(
      filters.umbrella
             .filter(value => availableUmbrellas.find(umbrella => umbrella.name.toLowerCase() === value.toLowerCase())));
  const value = useMemo(() => fundUmbrella.filter(umbrella => availableUmbrellas.map(umbrellaOption => umbrellaOption.name?.toLowerCase())
                                                                                .includes(umbrella?.toLowerCase())), [fundUmbrella, availableUmbrellas]);
  const handleUmbrellaFilter = useCallback((filters: string[]) => {
    setFundUmbrella(filters);
    const payload = filters.map((filter: string) => filter.toLowerCase());
    actions.addUmbrellaFilter(payload as FilterActionPayload);
  }, [actions]);

  useEffect(() => {
    if (value.length > 0) {
      if (JSON.stringify(fundUmbrella) !== JSON.stringify(value)) {
        handleUmbrellaFilter(value);
      }
    } else if (fundUmbrella.length > 0) {
      handleUmbrellaFilter([])
    }
  }, [value, fundUmbrella, handleUmbrellaFilter]);

  return {
    onChange: (arrValue: string[]) => {
      setFundUmbrella(arrValue);
      const payload = arrValue.map((v: string) => v.toLowerCase());
      actions.addUmbrellaFilter(payload as FilterActionPayload);
    },
    clearFilter: () => setFundUmbrella([]),
    filterDelete: (value?: string | string[]) => setFundUmbrella(prevState => prevState.filter(fundUmbrellaOption => fundUmbrellaOption !== value)),
    value,
    labelId: 'dropdown-umbrella',
    placeholder: t('fund-list.choose'),
    label: t('fund-list.umbrella'),
    options: availableUmbrellas.map(umbrellaOption => umbrellaOption.name),
    enableListenerOfExternalValueChange: true,
    name: 'funds-umbrella-filter'
  }
}

const useHorizonFilterConfig = (actions: Actions, filters: FilterState): FundListFilterConfigMulti => {
  const [t] = useTranslation('components');
  const initialState = (filters.horizon as number[]).map(value => fundListOptions.horizon.find(horizon => horizon.filter.number === value)?.label)
                               .filter(value => value !== undefined)
  const [fundHorizon, setFundHorizon] = useState<string[]>(initialState as string[]);

  return {
    onChange: (value: string[]) => {
      setFundHorizon(value);
      actions.addHorizonFilter(value as FilterActionPayload);
    },
    filterDelete: (value?: string | string[]) => setFundHorizon(prevState => prevState.filter(horizonOption => horizonOption !== value)),
    clearFilter: () => setFundHorizon([]),
    placeholder: t('fund-list.choose'),
    options: fundListOptions.horizon.map(option => option.label),
    valueToLabel: (value: string) =>  {
      return toDropdownTranslateOptions(fundListOptions.horizon.map(option => option.label), 'fund-list.options.horizon', t)
        .find(item => item.value === value)?.label || value
    },
    value: fundHorizon,
    labelId: 'dropdown-horizon',
    label: t('fund-list.horizon'),
    enableListenerOfExternalValueChange: true,
    name: 'funds-horizon-filter',
  }
}

const useUnitCategoryFilterConfig = (actions: Actions, filters: FilterState): FundListFilterConfigUnitCategory => {
  const [t] = useTranslation('components');
  const getUnitCategoryLabel = () => fundListOptions.unitCategoryCode.find(option => option.code === filters.unitCategoryCode)?.label;
  const [unitCategoryValue, setUnitCategoryValue] = useState(getUnitCategoryLabel());

  return {
    onChange: (event: React.ChangeEvent<{ name?: string; value: unknown; }>) => {
      const unitCategoryCode = fundListOptions.unitCategoryCode.find(option => option.label === event.target.value )?.code;
      setUnitCategoryValue(event.target.value as string);
      actions.addUnitCategoryFilter(unitCategoryCode as unknown as FilterActionPayload);
    },
    options: toDropdownTranslateOptions(fundListOptions.unitCategoryCode.map(option => option.label), 'fund-list.options.unit-category', t),
    clearFilter: () => setUnitCategoryValue('code-a'),
    value: unitCategoryValue as string,
    label: t('fund-list.category'),
    labelId: 'dropdown-unit-category-code',
    listTitle: t('fund-list.unit-category-of-participation'),
    placeholderDisplayType: 'dynamic' as const,
    placeholder: t('fund-list.unit-category-of-participation'),
    name: 'funds-unit-category-filter'
  }
}

const useSearchFilterConfig = (actions: Actions, filters: FilterState): FundListFilterCofingSearch => {
  const [searchValue, setSearchValue] = useState(filters.search);

  return {
    onChange: (event: React.ChangeEvent<{ name?: string; value: unknown; }>) => {
      setSearchValue(event.target.value as string);
      actions.addSearchFilter(event.target.value as FilterActionPayload);
    },
    value: searchValue,
    clearFilter: () => setSearchValue(''),
    name: 'funds-search-filter'
  }
}

const useAutocompleteFilterConfig = (actions: Actions, visibleFunds: PurchasableFund[] | undefined): FundListFilterCofingAutocomplete => {
  const [t] = useTranslation('components');
  const [tt] = useTranslation('translation');
  const [autocompleteValue, setAutocompleteValue] = useState<AutocompleteOptionModel>({ title: '', id: '' });

  return {
    onChange: (option: AutocompleteOptionModel) => {
      setAutocompleteValue(option);
      actions.addSearchFilter(option.title as FilterActionPayload);
    },
    placeholder: t('fund-list.find-fund'),
    searchPlaceholder: tt('search'),
    options: visibleFunds ? visibleFunds.map(fund => ({
      id: fund.identifier,
      title: fund.name,
    })) : [],
    getOptionLabel: (option: AutocompleteOptionModel) => option.title as string,
    value: autocompleteValue,
    clearFilter: () => setAutocompleteValue({ title: '', id: '' }),
    name: 'funds-autocomplete-filter'
  }
}

const useAdequacyWithTargetMarketFilterConfig = (actions: Actions): FundListFilterCofingCheckbox => {
  const [isAdequacyWithTargetMarket, setIsAdequacyWithTargetMarket] = useState(false);

  return {
    onChange: (isChecked: boolean) => {
      setIsAdequacyWithTargetMarket(isChecked);
      actions.addAdequacyWithTargetMarketFilter(isChecked);
    },
    value: isAdequacyWithTargetMarket,
    checked: isAdequacyWithTargetMarket,
    clearFilter: () => setIsAdequacyWithTargetMarket(false)
  }
}

const useWalletFilterConfig = (actions: Actions): FundListFilterCofingCheckbox => {
  const [isInWallet, setIsInWallet] = useState(false);

  return {
    onChange: (isChecked: boolean) => {
      setIsInWallet(isChecked);
      actions.addWalletFilter(isChecked);
    },
    value: isInWallet,
    checked: isInWallet,
    clearFilter: () => setIsInWallet(false)
  }
}

const useEsgFilterConfig = (actions: Actions): FundListFilterCofingCheckbox => {
  const [isEsg, setIsEsg] = useState(false);

  return {
    onChange: (isChecked: boolean) => {
      setIsEsg(isChecked);
      actions.addEsgFilter(isChecked);
    },
    value: isEsg,
    checked: isEsg,
    clearFilter: () => setIsEsg(false)
  }
}

export const useFundListFilter =(
  actions: Actions, filters: FilterState, visibleFunds: PurchasableFund[] | undefined, allAvailableFunds: PurchasableFund[] | undefined, initialFilterFundType: string | undefined, fundsState: { risks: Risk[]; fundTypes: FundType[]; umbrellas: Umbrella[] },
): FundListFilterConfig => {
  const riskConfig = useRiskFilterConfig(actions, fundsState.risks, filters);
  const fundTypeConfig = useFundTypeFilterConfig(actions, initialFilterFundType, fundsState.fundTypes, filters);
  const umbrellaConfig = useFundUmbrellaFilterConfig(actions, allAvailableFunds, fundsState.umbrellas, filters);
  const horizonConfig = useHorizonFilterConfig(actions, filters);
  const unitCategoryConfig = useUnitCategoryFilterConfig(actions, filters);
  const searchConfig = useSearchFilterConfig(actions, filters);
  const autocompleteConfig = useAutocompleteFilterConfig(actions, visibleFunds);
  const adequacyWithTargetMarketConfig = useAdequacyWithTargetMarketFilterConfig(actions);
  const walletConfig = useWalletFilterConfig(actions);
  const esgConfig = useEsgFilterConfig(actions);

  const clearFilters = () => {
    riskConfig.clearFilter();
    fundTypeConfig.clearFilter();
    umbrellaConfig.clearFilter();
    horizonConfig.clearFilter();
    searchConfig.clearFilter();
    autocompleteConfig.clearFilter();
    adequacyWithTargetMarketConfig.clearFilter();
    walletConfig.clearFilter();
    esgConfig.clearFilter();
    unitCategoryConfig.clearFilter();

    actions.clearFilters();
  };

  const isAnyFilter = [
    filters.horizon,
    fundTypeConfig.value,
    riskConfig.value, umbrellaConfig.value,
    horizonConfig.value, searchConfig.value,
    autocompleteConfig.value.title as string]
          // @ts-ignore
          .some(filter => filter.length > 0)
    || adequacyWithTargetMarketConfig.value
    || walletConfig.value || esgConfig.value;

  return {
    risk: riskConfig,
    fundType: fundTypeConfig,
    umbrella: umbrellaConfig,
    horizon: horizonConfig,
    unitCategory: unitCategoryConfig,
    search: searchConfig,
    autocomplete: autocompleteConfig,
    adequacyWithTargetMarket: adequacyWithTargetMarketConfig,
    wallet: walletConfig,
    esg: esgConfig,
    clearFilters,
    isAnyFilter
  }
}
